import TouchableRippleM from "@common/components/button/TouchableRippleM"
import { allOptionsFromSectionOptions, isSameArray } from "@data/utility/Utils"
import React, { useContext, useEffect, useState } from "react"
import { View } from "react-native"
import { theme } from "../../theme/theme"
import { DimensionsContext } from "../../utils/mobileUtils"
import IconButtonM from "../button/IconButtonM"
import { IconTextM } from "../icons/IconTextM"
import { SelectDateTime, SelectDateTimeRange } from "../select/selectDateTime"
import { SelectModalM } from "../select/selectSingle-Multiple"
import TextM from "../text/TextM"
import { ScrollMenuM } from "./ScrollMenuM"

export const MenuFilter = ({
    filterItems,
    position,
    style,
    formValues,
    setFormValues,
    onFinishSelecting,
    noMenu = false,
}) => {
    const dimensions = useContext(DimensionsContext)

    const defaultValues = filterItems?.reduce((pValue, cValue) => {
        const nValue = { ...pValue }
        nValue[cValue?.name] = cValue?.defaultValue ?? null
        if (cValue?.optionalField) {
            nValue[cValue?.optionalField?.name] =
                cValue?.optionalField?.defaultValue ?? null
        }
        return nValue
    }, {})

    const resetValues = filterItems?.reduce((pValue, cValue) => {
        const nValue = { ...pValue }
        nValue[cValue?.name] = cValue?.resetValue ?? null
        return nValue
    }, {})

    const width = style?.width ?? dimensions?.width

    useEffect(() => {
        if (!formValues) setFormValues(defaultValues)
    }, [])

    const [modalVisible, setModalVisible] = useState(false)
    const [currentItemIndex, setCurrentItemIndex] = useState(-1)

    const isDateTimeFilter =
        currentItemIndex >= 0
            ? filterItems?.[currentItemIndex]?.type === "select.dateTime"
            : false
    const isRangeSelectFilter =
        currentItemIndex >= 0
            ? filterItems?.[currentItemIndex]?.type === "select.range"
            : false
    const currentTitle =
        currentItemIndex >= 0
            ? "Filtrer par " + filterItems?.[currentItemIndex]?.label
            : ""
    const currentOptions =
        currentItemIndex >= 0 ? filterItems?.[currentItemIndex]?.options : []
    const currentSelectedValues =
        currentItemIndex >= 0
            ? (formValues?.[filterItems?.[currentItemIndex]?.name] ?? [])
            : []
    const currentSectionMode =
        currentItemIndex >= 0
            ? filterItems?.[currentItemIndex]?.sectionMode
            : false
    const isSingleSelect =
        currentItemIndex >= 0
            ? filterItems?.[currentItemIndex]?.singleSelect
            : false
    const currentOptionalField =
        currentItemIndex >= 0
            ? filterItems?.[currentItemIndex]?.optionalField
            : null

    // Multi-select logic
    const onMultiSelect = (item) => {
        if (currentItemIndex < 0 || isSingleSelect) return

        if (currentSelectedValues?.includes(item?.value)) {
            const x = [...currentSelectedValues]
            const index = x.indexOf(item?.value)
            x.splice(index, 1)
            setFormValues({
                ...formValues,
                [filterItems?.[currentItemIndex]?.name]: x,
            })
        } else {
            const x = [...currentSelectedValues, item?.value]
            setFormValues({
                ...formValues,
                [filterItems?.[currentItemIndex]?.name]: x,
            })
        }
    }

    // Single-select logic
    const onSingleSelect = (item, closeModal = true) => {
        if (currentItemIndex < 0 || !isSingleSelect) {
            return
        }

        const newFormValues = {
            ...formValues,
            [filterItems?.[currentItemIndex]?.name]: item?.value,
        }

        setFormValues(newFormValues)

        if (closeModal) {
            setModalVisible(false)
            onFinishSelecting(newFormValues)
        }
    }

    const onOptionSelect = (name, value) => {
        const newFormValues = {
            ...formValues,
            [name]: value,
        }

        setFormValues(newFormValues)
    }

    const selectMultipleItems = (items, forceDeselect = false) => {
        if (currentItemIndex < 0) return

        const x = [...currentSelectedValues]
        items?.forEach((item) => {
            if (forceDeselect && currentSelectedValues?.includes(item?.value)) {
                const index = x.indexOf(item?.value)
                x.splice(index, 1)
            } else if (!currentSelectedValues?.includes(item?.value)) {
                x?.push(item?.value)
            }
        })
        setFormValues({
            ...formValues,
            [filterItems?.[currentItemIndex]?.name]: x,
        })
    }

    const menuItems = [
        ...filterItems.map((filterItem, index) => {
            return {
                ...filterItem,
                title: filterItem.label,
                onPress: () => {
                    setCurrentItemIndex(index)
                    setModalVisible(true)
                },
            }
        }),
        {
            type: "divider",
        },
        {
            title: "Effacer",
            icon: "times",
            onPress: () => {
                setFormValues(resetValues)
                onFinishSelecting(resetValues)
            },
        },
    ]

    const onRemoveFilter = (index) => {
        // Remove this filter: clear multi-select to [], single-select to null
        const filterName = filterItems[index].name
        const filterOptionName = filterItems[index]?.optionalField?.name ?? null
        const newFormValues = { ...formValues }
        const currentVal = formValues[filterName]
        newFormValues[filterName] = Array.isArray(currentVal) ? [] : null
        if (filterOptionName) {
            newFormValues[filterOptionName] =
                filterItems[index]?.optionalField?.defaultValue ?? null
        }
        setFormValues(newFormValues)
        onFinishSelecting(newFormValues)
    }

    const renderButton = () => {
        const isFilterCleared = filterItems?.reduce((pValue, cValue) => {
            return (
                pValue &&
                isSameArray(
                    allOptionsFromSectionOptions(cValue?.options),
                    formValues?.[cValue?.name]
                )
            )
        }, true)

        return (
            <View
                style={{
                    width: width - 20,
                    flexDirection: "row",
                    alignItems: "center",
                    alignSelf: "center",
                }}
            >
                <IconTextM
                    text={isFilterCleared ? `Filtrer` : ""}
                    icon={"filter"}
                    style={{
                        width: null,
                        marginTop: 10,
                        marginBottom: 10,
                    }}
                    textStyle={{ width: null }}
                    color={theme.colors.light}
                />
                {filterItems.map((filterItem, index) => {
                    const currentValue = formValues?.[filterItem?.name]
                    const isMultiSelect = Array.isArray(currentValue) // Check if it's multi-select based on whether currentValue is an array

                    const isSingleSelectApplied =
                        !isMultiSelect &&
                        currentValue !== null &&
                        currentValue !== undefined

                    const isMultiSelectApplied =
                        isMultiSelect &&
                        currentValue?.length > 0 &&
                        !isSameArray(
                            allOptionsFromSectionOptions(filterItem?.options),
                            currentValue
                        )

                    if (isSingleSelectApplied || isMultiSelectApplied) {
                        return (
                            <TouchableRippleM
                                key={index}
                                style={{
                                    borderRadius: 10,
                                    borderWidth: 1,
                                    borderColor: theme.colors.light,
                                    paddingVertical: 3,
                                    paddingHorizontal: 5,
                                    marginLeft: 5,
                                    marginTop: 5,
                                    alignSelf: "center",
                                }}
                                onPress={() => {
                                    setCurrentItemIndex(index)
                                    setModalVisible(true)
                                }}
                            >
                                <View
                                    style={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                    }}
                                >
                                    <TextM
                                        style={{ maxWidth: 80 }}
                                        fontSize={11}
                                    >
                                        {filterItem?.label}
                                    </TextM>
                                    <IconButtonM
                                        icon="times"
                                        size={11}
                                        onPress={() => {
                                            onRemoveFilter(index)
                                        }}
                                        iconColor={theme.colors.grey}
                                        style={{
                                            padding: 0,
                                            margin: 0,
                                            marginLeft: 4,
                                        }}
                                    />
                                </View>
                            </TouchableRippleM>
                        )
                    } else return null
                })}
            </View>
        )
    }

    const renderFilterModal = () => {
        if (isDateTimeFilter) {
            return (
                <SelectDateTime
                    modalVisible={modalVisible}
                    setModalVisible={setModalVisible}
                    title={currentTitle}
                    itemHeight={70}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    currentFieldName={filterItems?.[currentItemIndex]?.name}
                    onFinishSelecting={() => {
                        setCurrentItemIndex(-1)
                        onFinishSelecting()
                    }}
                    optionalField={currentOptionalField}
                    onOptionSelect={onOptionSelect}
                />
            )
        }

        if (isRangeSelectFilter) {
            const handleRangeSelect = (
                rangeValues,
                currentValue,
                selectedDateValue,
                formValues,
                currentFieldName
            ) => {
                if (rangeValues.length > 0) {
                    const newValues = [...currentValue]
                    const existingDayIndex = newValues.findIndex(
                        (x) => x?.split(" ")[0] === selectedDateValue
                    )

                    const rangeStart = rangeValues[0]
                    const rangeEnd = rangeValues[rangeValues.length - 1]
                    const newTimeValue = `${selectedDateValue} ${rangeStart}-${rangeEnd}`

                    if (existingDayIndex >= 0) {
                        newValues[existingDayIndex] = newTimeValue
                    } else {
                        newValues.push(newTimeValue)
                    }

                    setFormValues({
                        ...formValues,
                        [currentFieldName]: newValues,
                    })
                }
            }

            return (
                <SelectDateTimeRange
                    modalVisible={modalVisible}
                    setModalVisible={setModalVisible}
                    title={currentTitle}
                    itemHeight={70}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    currentFieldName={filterItems?.[currentItemIndex]?.name}
                    onFinishSelecting={() => {
                        setCurrentItemIndex(-1)
                        onFinishSelecting()
                    }}
                    handleRangeSelect={handleRangeSelect}
                />
            )
        }

        return (
            <SelectModalM
                isMulti={!isSingleSelect}
                valueMode
                sectionMode={currentSectionMode}
                modalVisible={modalVisible}
                setModalVisible={setModalVisible}
                title={currentTitle}
                options={currentOptions}
                itemHeight={70}
                selectedValue={isSingleSelect ? currentSelectedValues : null}
                selectedValues={currentSelectedValues}
                onMultiSelect={onMultiSelect}
                onSingleSelect={onSingleSelect}
                selectMultipleItems={selectMultipleItems}
                onFinishSelecting={() => {
                    setCurrentItemIndex(-1)
                    if (!isSingleSelect) {
                        onFinishSelecting()
                    }
                }}
                formValues={formValues}
                optionalField={currentOptionalField}
                onOptionSelect={onOptionSelect}
            />
        )
    }

    return (
        <View
            style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
                width: width,
                marginTop: 20,
                ...style,
            }}
        >
            {!noMenu && (
                <ScrollMenuM
                    buttonComponent={renderButton()}
                    items={menuItems}
                    position={position}
                    style={{
                        alignSelf: "flex-end",
                    }}
                />
            )}
            {renderFilterModal()}
        </View>
    )
}
